import {
  put,
  delay,
  takeEvery,
  all,
  call,
  select,
  race,
  cancelled,
} from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setPicTreeReviewMethodBySeason,
  setPicTreeReviewSummaryDataBySeason,
  setPicTreeReviewTrendDataBySeason,
} from "actions";
import { getJwtToken, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import * as H from "helper";
import { PICTREE_REVIEW_MAP } from "../../config/pictreeReviewConfig";

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTree;
export const selectedSeason = (state) => state.pictree.selectedSeason;
export const reviewMethodBySeason = (state) =>
  state.pictree.reviewMethodBySeason;

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000, // 30s
  retries: 3, // 合計 4 回までトライ（初回  リトライ3）
  backoffBase: 1500, // 1.5s指数バックオフ
};

const SXI_TREND_RETRY = {
  timeoutMs: 28000,
  retries: 0, // 1回だけ
  backoffBase: 1500,
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000, // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2, // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000, // 1秒
    backoffFactor = 2, // 2倍ずつ
    jitter = true, // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(
        backoffBase * Math.pow(backoffFactor, attempt - 1),
        10000
      );
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

const buildCompletedQuery = (gameSpaceId, flowType, startUtc, endUtc) => `
  SELECT
    COUNT(*) AS completed_count
  FROM review_request rr
  WHERE
    rr.game_space_id     = '${gameSpaceId}'
    AND rr.review_flow_type = '${flowType}'
    AND EXISTS (
      SELECT 1
      FROM review_request_event rre
      WHERE rre.review_request_id = rr.id
        AND rre.type IN ('APPROVED', 'REJECTED')
        AND rre.occurred_datetime BETWEEN '${startUtc}' AND '${endUtc}'
    );
`;

const buildUncompletedQuery = (gameSpaceId, flowType, startUtc, endUtc) => `
  SELECT
    COUNT(*) AS uncompleted_count
  FROM review_request rr
  WHERE
    rr.game_space_id        = '${gameSpaceId}'
    AND rr.review_flow_type = '${flowType}'
    -- 期間内に何かイベントがある（元クエリの JOIN  日付条件に相当）
    AND EXISTS (
      SELECT 1
      FROM review_request_event rre
      WHERE rre.review_request_id = rr.id
        AND rre.occurred_datetime BETWEEN '${startUtc}' AND '${endUtc}'
    )
    -- 一度も APPROVED/REJECTED が無い（元クエリの NOT EXISTS と同じ）
    AND NOT EXISTS (
      SELECT 1
      FROM review_request_event rre_sub
      WHERE rre_sub.review_request_id = rr.id
        AND rre_sub.type IN ('APPROVED', 'REJECTED')
    );
`;

const buildAiNotCompletedQuery = (gameSpaceId, startUtc, endUtc) => `
  SELECT
    rr.game_space_id,
    COUNT(DISTINCT rr.id) AS count_ai_review_not_completed
  FROM review_request rr
  INNER JOIN review_request_event rre
    ON rre.review_request_id = rr.id
  WHERE
    rr.game_space_id = '${gameSpaceId}'
    AND rr.review_flow_type = 'OPERATOR_REVIEW_IF_AI_REVIEW_NG'
    AND rre.occurred_datetime BETWEEN '${startUtc}' AND '${endUtc}'
    AND NOT EXISTS (
      SELECT 1
      FROM review_request_event rre2
      WHERE rre2.review_request_id = rr.id
        AND rre2.type = 'AI_REVIEW_COMPLETED'
    )
`;

const buildAiCompletedButNotOpCompletedQuery = (
  gameSpaceId,
  startUtc,
  endUtc
) => `
  SELECT
    rr.game_space_id,
    COUNT(DISTINCT rr.id) AS count_ai_completed_but_not_approved_or_rejected
  FROM review_request rr
  INNER JOIN review_request_event rre
    ON rre.review_request_id = rr.id
  WHERE
    rr.game_space_id = '${gameSpaceId}'
    AND rr.review_flow_type = 'OPERATOR_REVIEW_IF_AI_REVIEW_NG'
    AND rre.occurred_datetime BETWEEN '${startUtc}' AND '${endUtc}'
    AND rre.type = 'AI_REVIEW_COMPLETED'
    AND NOT EXISTS (
      SELECT 1
      FROM review_request_event rret
      WHERE rret.review_request_id = rr.id
        AND rret.type IN ('APPROVED', 'REJECTED')
    )
`;

// review 日別/時間別トレンド（完了・未完了）取得（チューニング版）
export const buildReviewTrendTrendQuery = (
  gameSpaceId,
  flowType,
  startUtc,
  endUtc,
  chartSize = "day"
) => {
  const bucketExprCompleted =
    chartSize === "hour"
      ? "DATE_FORMAT(CONVERT_TZ(c.completed_at_utc, '+00:00', '+09:00'), '%Y-%m-%d %H:00:00')"
      : "DATE(CONVERT_TZ(c.completed_at_utc, '+00:00', '+09:00'))";

  const bucketExprUncompleted =
    chartSize === "hour"
      ? "DATE_FORMAT(CONVERT_TZ(f.first_event_at_utc, '+00:00', '+09:00'), '%Y-%m-%d %H:00:00')"
      : "DATE(CONVERT_TZ(f.first_event_at_utc, '+00:00', '+09:00'))";

  return `
    -- まず対象の review_request だけを絞り込む
    WITH target_rr AS (
      SELECT id
      FROM review_request
      WHERE game_space_id = '${gameSpaceId}'
        AND review_flow_type = '${flowType}'
    ),
    completed AS (
      SELECT
          rr.id,
          MIN(rre.occurred_datetime) AS completed_at_utc
      FROM target_rr rr
      JOIN review_request_event rre
        ON rre.review_request_id = rr.id
      WHERE
        rre.type IN ('APPROVED', 'REJECTED')
        AND rre.occurred_datetime BETWEEN '${startUtc}' AND '${endUtc}'
      GROUP BY rr.id
    ),
    first_event AS (
      SELECT
          rr.id,
          MIN(rre.occurred_datetime) AS first_event_at_utc
      FROM target_rr rr
      JOIN review_request_event rre
        ON rre.review_request_id = rr.id
      WHERE
        rre.occurred_datetime BETWEEN '${startUtc}' AND '${endUtc}'
        -- 「一度も APPROVED/REJECTED が無い rr だけ」をここで絞る
        AND NOT EXISTS (
          SELECT 1
          FROM review_request_event rre_sub
          WHERE rre_sub.review_request_id = rr.id
            AND rre_sub.type IN ('APPROVED', 'REJECTED')
        )
      GROUP BY rr.id
    )
    SELECT
      t.time_bucket_jst,
      SUM(CASE WHEN t.status = 'completed'   THEN t.cnt ELSE 0 END) AS completed_count,
      SUM(CASE WHEN t.status = 'uncompleted' THEN t.cnt ELSE 0 END) AS uncompleted_count
    FROM (
      SELECT
        ${bucketExprCompleted} AS time_bucket_jst,
        COUNT(*) AS cnt,
        'completed' AS status
      FROM completed c
      GROUP BY time_bucket_jst

      UNION ALL

      SELECT
        ${bucketExprUncompleted} AS time_bucket_jst,
        COUNT(*) AS cnt,
        'uncompleted' AS status
      FROM first_event f
      GROUP BY time_bucket_jst
    ) t
    GROUP BY t.time_bucket_jst
    ORDER BY t.time_bucket_jst;
  `;
};

export function* getPicTreeReviewMethodBySeason(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const selectedSeasonData = yield select(selectedSeason);

    // ① レビュー設定（flow type 一覧）
    const sqlQueryReviewMethod = {
      query: `
        SELECT * FROM review_flow_setting WHERE game_space_id = '${selectedSeasonData[0].seasonId}'
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [raw] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [sqlQueryReviewMethod, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    const uniqueFlowTypes = Array.from(
      new Set(raw.map((method) => method.review_flow_type))
    );

    yield put(setPicTreeReviewMethodBySeason(uniqueFlowTypes));
  } catch (err) {
    console.log(err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPicTreeReviewSummaryDataBySeason(action) {
  try {
    yield put(setPageTopLoader(true));
    yield put(setPicTreeReviewSummaryDataBySeason([]));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const reviewMethodData = yield select(reviewMethodBySeason);

    if (reviewMethodData.length === 0) {
      console.log(
        "Log: No review method data available, skipping summary data fetch."
      );
      return;
    }

    const startUtc = moment
      .tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const gameSpaceId = selectedSeasonData[0].seasonId;

    // UI 表示用ラベル
    const baseReviewMethods = reviewMethodData.map((flowType) => ({
      review_flow_type: flowType,
      reviewTypeName: PICTREE_REVIEW_MAP[flowType] || "不明なレビュータイプ",
    }));

    // 1 「レビューなし」はクエリ不要なので除外
    const flowTypesForQuery = reviewMethodData.filter(
      (t) => t !== "WITHOUT_REVIEW"
    );

    // 2 投げるクエリ一覧を作成
    const sqlJobs = [];

    flowTypesForQuery.forEach((flowType) => {
      // 完了・未完了（共通）
      sqlJobs.push({
        flowType,
        kind: "completed",
        sql: buildCompletedQuery(gameSpaceId, flowType, startUtc, endUtc),
      });
      sqlJobs.push({
        flowType,
        kind: "uncompleted",
        sql: buildUncompletedQuery(gameSpaceId, flowType, startUtc, endUtc),
      });

      // AI  運用者フローだけ追加 2 クエリ
      if (flowType === "OPERATOR_REVIEW_IF_AI_REVIEW_NG") {
        sqlJobs.push({
          flowType,
          kind: "aiNotCompleted",
          sql: buildAiNotCompletedQuery(gameSpaceId, startUtc, endUtc),
        });
        sqlJobs.push({
          flowType,
          kind: "aiCompletedButNotOpCompleted",
          sql: buildAiCompletedButNotOpCompletedQuery(
            gameSpaceId,
            startUtc,
            endUtc
          ),
        });
      }
    });

    const responses = [];
    for (const job of sqlJobs) {
      const res = yield call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [
          {
            query: job.sql,
            database: "KpiDashboard",
            type: "list",
          },
          ENDPOINT.GET_DATA_SXI_DENCHU,
          jwtToken,
        ],
        SXI_HEAVY_RETRY
      );
      responses.push(res);
    }

    // 4 flow type ごとの集計
    const countsByFlowType = {};

    sqlJobs.forEach((job, index) => {
      const rows = responses[index] || [];
      const row = rows[0] || {};
      const ft = job.flowType;

      if (!countsByFlowType[ft]) {
        countsByFlowType[ft] = {
          completedCount: 0,
          uncompletedCount: 0,
          aiReviewNotCompleted: 0,
          aiCompletedButNotOperatorCompleted: 0,
        };
      }

      switch (job.kind) {
        case "completed":
          countsByFlowType[ft].completedCount = row.completed_count || 0;
          break;
        case "uncompleted":
          countsByFlowType[ft].uncompletedCount = row.uncompleted_count || 0;
          break;
        case "aiNotCompleted":
          countsByFlowType[ft].aiReviewNotCompleted =
            row.count_ai_review_not_completed || 0;
          break;
        case "aiCompletedButNotOpCompleted":
          countsByFlowType[ft].aiCompletedButNotOperatorCompleted =
            row.count_ai_completed_but_not_approved_or_rejected || 0;
          break;
        default:
      }
    });

    // 5 ラベル  集計結果をマージして reducer へ
    const mappedReviewMethodsWithCounts = baseReviewMethods.map((m) => ({
      ...m,
      ...(countsByFlowType[m.review_flow_type] || {}),
    }));

    // reducer へ保存（reviewMethodBySeason: [] に格納）
    yield put(
      setPicTreeReviewSummaryDataBySeason(mappedReviewMethodsWithCounts)
    );
  } catch (err) {
    console.log(err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPicTreeReviewTrendDataBySeason(action) {
  try {
    yield put(setPageTopLoader(true));
    yield put(setPicTreeReviewTrendDataBySeason([]));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const reviewMethodData = yield select(reviewMethodBySeason);
    const chartSize = action.payload || "day";

    if (
      !rangeTarget ||
      rangeTarget.length < 2 ||
      !selectedSeasonData ||
      selectedSeasonData.length === 0 ||
      !reviewMethodData ||
      reviewMethodData.length === 0
    ) {
      console.log("Not enough data to fetch review trend.");
      return;
    }

    const gameSpaceId = selectedSeasonData[0].seasonId;

    // 日数チェック （7日超はスキップ）
    const days = moment(rangeTarget[1]).diff(rangeTarget[0], "days");
    if (days > 7) {
      console.log("LOG: range too long (>7 days), skip trend query");
      return;
    }

    const startUtc = moment
      .tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const flowTypesForQuery = reviewMethodData.filter(
      (t) => t !== "WITHOUT_REVIEW"
    );

    const sqlJobs = flowTypesForQuery.map((flowType) => ({
      flowType,
      sql: buildReviewTrendTrendQuery(
        gameSpaceId,
        flowType,
        startUtc,
        endUtc,
        chartSize
      ),
    }));

    const responses = [];
    for (const job of sqlJobs) {
      const res = yield call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [
          {
            query: job.sql,
            database: "KpiDashboard",
            type: "list",
          },
          ENDPOINT.GET_DATA_SXI_DENCHU,
          jwtToken,
        ],
        SXI_TREND_RETRY
      );
      responses.push(res);
    }

    const trendByFlowType = {};

    sqlJobs.forEach((job, index) => {
      const rows = responses[index] || [];

      trendByFlowType[job.flowType] = rows.map((r) => ({
        bucketJst: r.time_bucket_jst, // 日 or 時間
        completedCount: Number(r.completed_count || 0),
        uncompletedCount: Number(r.uncompleted_count || 0),
      }));
    });
    yield put(setPicTreeReviewTrendDataBySeason(trendByFlowType));
  } catch (err) {
    console.log(err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export default function* watchPageSettings() {
  yield takeEvery(
    PICTREE.GET_REVIEW_METHOD_BY_SEASON,
    getPicTreeReviewMethodBySeason
  );
  yield takeEvery(
    PICTREE.GET_REVIEW_SUMMARY_DATA_BY_SEASON,
    getPicTreeReviewSummaryDataBySeason
  );
  yield takeEvery(
    PICTREE.GET_REVIEW_TREND_DATA_BY_SEASON,
    getPicTreeReviewTrendDataBySeason
  );
}
